import React, { useEffect, useMemo, useState } from 'react';
import { SearchIcon } from '@heroicons/react/solid';
import PatientHistoryPanel from '../components/PatientHistoryPanel';
import InformantHistoryPanel from '../components/InformantHistoryPanel';
import ProviderManageInformantsForm from '../components/ProviderManageInformantsForm';
import PatientScalesPanel from '../components/PatientScalesPanel';
import { PatientDataContext } from '../../../../context/PatientDataContext';
import { getOrderedAutismInformantIds } from '../../../../utils/informantFirestorePaths';

/**
 * Whether this patient has any rating scales assigned. Autism patients only get
 * scales when their org configures them (e.g. LMP: ASRS, AQ-50, PHQ-9, GAD-7,
 * BAARS-IV Childhood). TOC autism patients have none. We read the synthesised
 * "Patient Scales" section built by buildModularPatientOverviewModel — so the
 * panel is DATA-GATED on assigned scales, never on org name. Zero scales ⇒ no
 * panel, which preserves the existing TOC layout exactly.
 */
function hasAssignedPatientScales(patientDataModel) {
  const sections = patientDataModel?.dataModel?.['1_preAppointmentInformation']?.sections;
  if (!sections) return false;
  const scalesSection = Object.values(sections).find(
    (section) => section?.internalName === 'Patient Scales',
  );
  return Boolean(scalesSection?.domains && Object.keys(scalesSection.domains).length > 0);
}

/**
 * Build a short tab label for an informant (name + optional relationship).
 */
function getInformantTabLabel(informantId, mapEntry, enriched, index) {
  const name = mapEntry?.informantName
    || enriched?.displayName
    || [enriched?.firstName, enriched?.lastName].filter(Boolean).join(' ')
    || enriched?.email
    || `Informant ${index + 1}`;

  const relationship = mapEntry?.informantRelationshipToPatient;
  if (relationship) {
    return `${name} (${relationship})`;
  }
  return name;
}

/**
 * True when the patient finished Autism informant setup but chose not to add
 * anyone. Autism stores that choice as informantSkipReasons.informant1 (unlike
 * ADHD's adulthood/childhood keys). We treat "setup complete + zero informants"
 * as the skip signal so clinicians still see the empty-state messaging even if
 * the reason string is missing for any reason.
 */
function didPatientSkipInformants(patientDataModel) {
  const hasNoInformants = !patientDataModel?.informants
    || Object.keys(patientDataModel.informants).length === 0;
  return Boolean(patientDataModel?.hasCompletedInformantSetupForm && hasNoInformants);
}

/**
 * AutismPreAppointmentDashboard
 *
 * Adult Autism TOC patients have no pre-appointment rating scales — only
 * patient and informant history questionnaires. This layout shows patient
 * history on the left and informant history on the right (mirroring the
 * two-column flex pattern from PreAppointmentDashboardWithData, but without
 * PatientScalesPanel).
 *
 * When two informants are assigned, the right panel uses the same underline-tab
 * pattern as elsewhere in the app (e.g. ServiceUserDashboard) to switch views.
 *
 * Empty-state parity with ADHD PatientScalesPanel: if the patient completed
 * setup but skipped adding informants, show that clearly and let the clinician
 * open Manage Informants to read the stored skip reason.
 */
export default function AutismPreAppointmentDashboard({
  patientDataModel,
  informantData = {},
  patientId,
  refreshPatientData,
  onView,
  displayMode = 'full',
}) {
  const orderedInformantIds = useMemo(
    () => getOrderedAutismInformantIds(patientDataModel?.informants || {}),
    [patientDataModel?.informants],
  );

  const [activeInformantId, setActiveInformantId] = useState(null);
  // Same drill-in pattern as ADHD's informant scales empty state: magnifying
  // glass opens Manage Informants so the clinician can read the skip reason.
  const [isManageInformantsOpen, setIsManageInformantsOpen] = useState(false);

  const patientSkippedInformants = didPatientSkipInformants(patientDataModel);

  // Only autism orgs that assign rating scales (e.g. LMP) show the scales panel.
  const showScalesPanel = useMemo(
    () => hasAssignedPatientScales(patientDataModel),
    [patientDataModel],
  );

  // PatientScalesPanel is an ADHD-era component that reads fetchPatientData from
  // PatientDataContext (for its refresh action). The autism route uses the
  // modular provider instead, so we supply a minimal context that maps that
  // refresh onto the modular refreshPatientData. We hide the informant-scales
  // half because autism has no informant rating scales and shows informant
  // history separately below.
  const scalesContextValue = useMemo(
    () => ({ fetchPatientData: () => refreshPatientData?.() }),
    [refreshPatientData],
  );

  useEffect(() => {
    if (onView && typeof onView === 'function') {
      onView();
    }
  }, [onView]);

  // Keep the selected tab valid when informants are added or removed.
  useEffect(() => {
    if (orderedInformantIds.length === 0) {
      setActiveInformantId(null);
      return;
    }
    if (!activeInformantId || !orderedInformantIds.includes(activeInformantId)) {
      setActiveInformantId(orderedInformantIds[0]);
    }
  }, [orderedInformantIds, activeInformantId]);

  const activeInformantView = activeInformantId ? informantData[activeInformantId] : null;

  const informantTabs = useMemo(() => {
    if (orderedInformantIds.length <= 1) return null;
    return orderedInformantIds.map((informantId, index) => {
      const mapEntry = patientDataModel.informants[informantId];
      const enriched = informantData[informantId];
      return {
        id: informantId,
        label: getInformantTabLabel(informantId, mapEntry, enriched, index),
      };
    });
  }, [orderedInformantIds, patientDataModel?.informants, informantData]);

  // When there's only one informant, extract their name and relationship for the panel header.
  const singleInformantMeta = useMemo(() => {
    if (orderedInformantIds.length !== 1) return null;
    const uid = orderedInformantIds[0];
    const mapEntry = patientDataModel?.informants?.[uid];
    const enriched = informantData[uid];

    const displayName = mapEntry?.informantName
      || enriched?.displayName
      || [enriched?.firstName, enriched?.lastName].filter(Boolean).join(' ')
      || enriched?.email
      || null;

    const relationship = mapEntry?.informantRelationshipToPatient || null;

    return { displayName, relationship };
  }, [orderedInformantIds, patientDataModel?.informants, informantData]);

  return (
    <div className="flex flex-col gap-5">
      {showScalesPanel && (
        <PatientDataContext.Provider value={scalesContextValue}>
          <PatientScalesPanel
            patientData={patientDataModel}
            informantData={informantData}
            displayMode={displayMode}
            hideInformantScales
          />
        </PatientDataContext.Provider>
      )}

      <div className="flex flex-col lg:flex-row justify-between gap-3 lg:gap-5">
        <div className="w-full lg:w-1/2 lg:grow lg:shrink lg:basis-0">
          <PatientHistoryPanel
            patientData={patientDataModel}
            displayMode={displayMode}
          />
        </div>

        <div className="w-full lg:w-1/2 lg:grow lg:shrink lg:basis-0">
          {orderedInformantIds.length === 0 ? (
            <div className="w-full p-5 bg-white rounded-xl shadow border border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Informant History</h2>
              {/* Distinguish "not set up yet" from "completed setup but skipped".
                  The latter mirrors ADHD PatientScalesPanel so clinicians can
                  open Manage Informants and read informantSkipReasons.informant1. */}
              {patientSkippedInformants ? (
                <div className="flex items-center justify-start space-x-1.5">
                  <p className="text-left text-sm text-gray-600">
                    The patient has skipped adding informants.
                  </p>
                  <button
                    type="button"
                    onClick={() => setIsManageInformantsOpen(true)}
                    className="inline-flex items-center justify-center rounded-full p-1 text-violet-600 hover:bg-violet-100 hover:text-violet-800 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-violet-500"
                    title="View skip reasons"
                    aria-label="View skip reasons in Manage Informants"
                  >
                    <SearchIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <p className="text-left text-sm text-gray-600">
                  No informants have been set up for this patient yet. Informant history
                  questionnaires will appear here once an informant is added and starts
                  completing their forms.
                </p>
              )}
            </div>
          ) : (
            <InformantHistoryPanel
              key={activeInformantId}
              informantId={activeInformantId}
              informantViewData={activeInformantView}
              patientData={patientDataModel}
              displayMode={displayMode}
              informantTabs={informantTabs}
              activeInformantTabId={activeInformantId}
              onInformantTabChange={setActiveInformantId}
              informantDisplayName={singleInformantMeta?.displayName}
              informantRelationship={singleInformantMeta?.relationship}
            />
          )}
        </div>
      </div>

      {/* Opened from the skipped-informants empty state so providers can review
          the patient's stated reason without leaving Patient Overview. */}
      <ProviderManageInformantsForm
        isOpen={isManageInformantsOpen}
        onClose={() => setIsManageInformantsOpen(false)}
        patientDataModel={patientDataModel}
        patientId={patientId}
        refreshPatientData={refreshPatientData}
      />
    </div>
  );
}
